import * as z from 'zod'

import { pastPlansSchema, subscriptionSchema } from 'src/shared/features/subscriptions/schemas/subscriptions'
import { ClientFocusSchema } from 'src/shared/features/users/schemas/client-focus'
import { expertiseSchema } from 'src/shared/features/users/schemas/expertise'
import { languageSchema } from 'src/shared/features/users/schemas/language'
import { statesSchema } from 'src/shared/features/users/schemas/states'
import { ROLES } from 'src/shared/features/users/types/roles'

import type { Profession } from './profession'
import { professionMap } from './profession'
import { twilioOnboardingStatusSchema } from './twilio'

//--- Base Field Schemas ---//

export const firstNameSchema = z
  .string()
  .min(2, 'First name must be at least 2 characters')
  .max(15, 'First name must be at most 15 characters')
  .regex(/^[a-zA-Z-]+$/, 'First name can only contain letters and dashes (no spaces or other special characters)')
  .refine((val) => (val.match(/-/g) || []).length <= 1, 'First name can only contain one dash')

export const lastNameSchema = z
  .string()
  .min(2, 'Last name must be at least 2 characters')
  .max(20, 'Last name must be at most 20 characters')
  .regex(/^[a-zA-Z-]+$/, 'Last name can only contain letters and dashes (no spaces or other special characters)')
  .refine((val) => (val.match(/-/g) || []).length <= 1, 'Last name can only contain one dash')

export const usernameSchema = z
  .string()
  .min(3, 'Username must be at least 3 characters')
  .max(20, 'Username must be at most 20 characters')
  .regex(
    /^[a-z0-9_]+$/,
    'Can only contain undercase letters, numbers, and underscores (no spaces or special characters)',
  )
  .refine((val) => !val.includes(' '), 'Username cannot contain spaces')
  .refine((val) => (val.match(/[a-z]/g) || []).length >= 3, 'Username must contain at least 3 letters')

//--- Onboarding Schemas ---//

export const languagesFormSchema = z.object({
  languages: z.array(languageSchema).min(1, 'Select at least one language'),
})

const professionSchema = z.enum(Object.keys(professionMap) as [Profession, ...Profession[]], {
  errorMap: () => ({ message: 'Please select a profession' }),
})

export const professionFormSchema = z.object({
  profession: professionSchema,
})

export const firstNameFormSchema = z.object({
  firstName: firstNameSchema,
})

export const lastNameFormSchema = z.object({
  lastName: lastNameSchema,
})

export const nameFormSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
})

export const usernameFormSchema = z.object({
  username: usernameSchema,
})

export const addressFormSchema = z.object({
  streetAddress1: z.string().min(1, 'Street is required'),
  streetAddress2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(5, 'ZIP code must be at least 5 characters'),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
})

export const onboardingSchema = z.object({
  username: usernameSchema,
  primaryEmail: z.string().email().optional(),
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  profession: professionSchema,
  phoneNumber: z.string().optional(),
})

//--- Update User Schemas ---//

// New schema for state-license pairs
export const stateLicensePairSchema = z.object({
  state: statesSchema,
  licenseNumber: z.string(),
})

export const updateProfileSchema = z.object({
  firstName: firstNameSchema.optional(),
  lastName: lastNameSchema.optional(),
  biography: z.string().max(1000, 'Biography must be at most 1000 characters').optional(),
  expertise: z.array(expertiseSchema).max(2, 'Maximum of 2 expertise allowed').optional(),
  languages: z.array(languageSchema).optional(),
  companyName: z.string().optional(),
  careerStartDate: z.string().date().optional(),
  dateOfBirth: z.string().date().optional(),
  streetAddress1: z.string(),
  streetAddress2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  commissionSplit: z.number().optional(),
  clientFocus: ClientFocusSchema.optional(),
  phoneNumber: z.string().optional(),
})

export const updateAccountSchema = z.object({
  username: usernameSchema.optional(),
  primaryEmail: z.string().email().optional(),
  phoneNumber: z.string().optional(),
})

export const updateUserPhotoSchema = z.object({
  key: z.string(),
  photoType: z.enum(['profile_photo', 'cover_photo']),
})

//--- Read User Schemas ---// //TODO: these can just be interfaces

export const publicUserSchema = z.object({
  id: z.number(),
  profession: professionSchema,
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  fullName: z.string(),
  shortAddress: z.string().optional(),
  state: z.string(),
  languages: z.array(languageSchema),
  biography: z.string().optional(),
  expertise: z.array(expertiseSchema),
  clientFocus: ClientFocusSchema.optional(), //TODO: make required for agents
  commissionSplit: z.number().optional(), //TODO: make required for agents
  verified: z.boolean(),
  totalPoints: z.number(),
  careerStartDate: z.string().date().optional(),
  dateOfBirth: z.string().date().optional(),
  companyName: z.string(),
  networkSize: z.number(),
  profilePhoto: z.string().url(),
  coverPhoto: z.string().url(),
  hasSentReferral: z.boolean(),
  hasReceivedReferral: z.boolean(),
  subscription: subscriptionSchema.optional(), // TODO: make a public version of this
  phoneNumber: z.string().optional(),
  calendlyUrl: z.string().optional(),
  customLinks: z
    .array(
      z.object({
        url: z.string().url(),
        title: z.string().max(20, 'Title must be at most 20 characters'),
      }),
    )
    .optional(),
  assignedTwilioPhoneNumber: z.string().optional(),
})

export const privateUserSchema = publicUserSchema.extend({
  streetAddress1: z.string(),
  streetAddress2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  primaryEmail: z.string().email(),
  profileComplete: z.number(),
  missingProfileFields: z.array(z.string()),
  fullAddress: z.string(),
  passwordUpdatedAt: z.string().date(),
  createdAt: z.string().date(),
  role: z.nativeEnum(ROLES),
  submittedLicense: z.boolean(),
  hasPastPlans: pastPlansSchema.optional(),
  visibilityScore: z.number().optional(),
  aiTuningProfile: z
    .object({
      brandDescription: z.string(),
      tone: z.string(),
      personalityTraits: z.array(z.string()),
      communicationStyle: z.string(),
      formalityLevel: z.string(),
      otherTuningNotes: z.string().optional(),
    })
    .optional(),
  weeklyAiMessageCount: z.number(),
  leadCredits: z.number(),
  temporaryEmail: z.string().email().optional(),
  calendlyUrl: z.string().optional(),
  phoneNumber: z.string().optional(),
  needsEnterpriseOnboarding: z.boolean().optional(),
  enterpriseId: z.number().optional(),
  twilioOnboardingSubmitted: z.boolean(),
  twilioOnboardingStatus: twilioOnboardingStatusSchema.optional(),
  twilioOnboardingDate: z.string().date().optional(),
  mfaEnabled: z.boolean(),
  authProviders: z.array(z.enum(['email', 'phone', 'google', 'apple', 'microsoft'])),
})

export const searchUsersSchema = z.object({
  id: z.number(),
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  city: z.string(),
  state: z.string(),
  profession: professionSchema,
})

//--- Type Aliases ---//

// form types
export type NameFormData = z.infer<typeof nameFormSchema>
export type FirstNameFormData = z.infer<typeof firstNameFormSchema>
export type LastNameFormData = z.infer<typeof lastNameFormSchema>
export type UsernameFormData = z.infer<typeof usernameFormSchema>
export type AddressFormData = z.infer<typeof addressFormSchema>
export type OnboardingData = z.infer<typeof onboardingSchema>

// user model types
export type PublicUserData = z.infer<typeof publicUserSchema>
export type PrivateUserData = z.infer<typeof privateUserSchema>
export type SearchUsersData = z.infer<typeof searchUsersSchema>
export type UpdateProfileFormData = z.infer<typeof updateProfileSchema>
export type UpdateAccountFormData = z.infer<typeof updateAccountSchema>
export type UpdateUserPhotoData = z.infer<typeof updateUserPhotoSchema>
export type MyUserDataResponse = {
  user: PrivateUserData
  tokens: {
    streamFeedToken: string
    streamChatToken: string
  }
}
